import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import Logo from '../Assets/LOGO.webp';
import productsData from '../data/products.json';

// Build a deduplicated list of searchable terms from products
const buildSuggestions = () => {
  const set = new Set();
  productsData.forEach((p) => {
    if (p.name) set.add(p.name);
    if (p.category) set.add(p.category);
    if (p.type) set.add(p.type.charAt(0).toUpperCase() + p.type.slice(1)); // "Dog" / "Cat"
  });
  return Array.from(set).sort();
};
const ALL_SUGGESTIONS = buildSuggestions();

/* ── Reusable SearchBox ─────────────────────────────── */
function SearchBox({ className, onSubmit }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIdx(-1);
    if (val.trim().length >= 1) {
      const filtered = ALL_SUGGESTIONS.filter((s) =>
        s.toLowerCase().includes(val.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setOpen(filtered.length > 0);
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        selectSuggestion(suggestions[activeIdx]);
      } else {
        submitSearch(query);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const selectSuggestion = (value) => {
    setQuery(value);
    setOpen(false);
    setSuggestions([]);
    submitSearch(value);
  };

  const submitSearch = (val) => {
    if (val.trim()) {
      onSubmit(val.trim());
      setQuery('');
      setOpen(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    submitSearch(query);
  };

  return (
    <div className={`search-wrapper ${className}`} ref={wrapperRef}>
      <form className="nav-search" onSubmit={handleFormSubmit}>
        <button type="submit" className="search-icon-btn" aria-label="Search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="7" stroke="#931A1D" strokeWidth="2"/>
            <path d="M20 20L17 17" stroke="#931A1D" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <input
          type="text"
          placeholder="Search products..."
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className="search-input"
          autoComplete="off"
          aria-label="Search products"
          aria-autocomplete="list"
        />
      </form>

      {open && suggestions.length > 0 && (
        <ul className="search-suggestions" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={s}
              className={`suggestion-item ${i === activeIdx ? 'active' : ''}`}
              onMouseDown={() => selectSuggestion(s)}
              role="option"
              aria-selected={i === activeIdx}
            >
              <span className="suggestion-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="7" stroke="#931A1D" strokeWidth="2"/>
                  <path d="M20 20L17 17" stroke="#931A1D" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="suggestion-text">{s}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Main Navbar ─────────────────────────────────────── */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          setScrolled(currentScrollY > 20);
          
          if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
            setVisible(false);
            setMobileMenuOpen(false);
          } else {
            setVisible(true);
          }
          lastScrollY.current = currentScrollY;
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileDropdown(null);
  };

  const toggleMobileDropdown = (e, menu) => {
    e.preventDefault();
    setMobileDropdown(mobileDropdown === menu ? null : menu);
  };

  const handleSearchSubmit = (query) => {
    navigate(`/products?search=${encodeURIComponent(query)}`);
    closeMobileMenu();
  };

  const params = new URLSearchParams(location.search);
  const filterVal = params.get('filter');
  const isDogActive = location.pathname.includes('/category/dog') || 
                      location.pathname === '/dog-products' || 
                      (location.pathname === '/products' && (filterVal === 'Dogs' || filterVal === 'dog'));
  const isCatActive = location.pathname.includes('/category/cat') || 
                      location.pathname === '/cat-products' || 
                      (location.pathname === '/products' && (filterVal === 'Cats' || filterVal === 'cat'));

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${!visible ? 'hidden' : ''}`}>
      <div className="navbar-container">

        {/* Logo — Left */}
        <div className="logo-section" onClick={closeMobileMenu}>
          <Link to="/" className="logo-link">
            <img
              src={Logo}
              alt="Canine Crown Logo"
              className="navbar-logo"
            />
          </Link>
        </div>

        {/* Mobile menu overlay */}
        <div
          className={`navbar-overlay ${mobileMenuOpen ? 'active' : ''}`}
          onClick={closeMobileMenu}
        ></div>

        {/* Nav Links — Center */}
        <div className={`nav-links-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <ul className="nav-links">
            <li className="nav-item" onClick={closeMobileMenu}>
              <NavLink
                to="/"
                className={({ isActive }) => `nav-link ${isActive && location.hash === '' ? 'active' : ''}`}
                end
              >
                Home
              </NavLink>
            </li>
            <li className="nav-item" onClick={closeMobileMenu}>
              <NavLink to="/about" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                About us
              </NavLink>
            </li>
            <li className="nav-item dropdown">
              <Link
                to="/products?filter=Dogs"
                className={`nav-link ${isDogActive ? 'active' : ''}`}
                onClick={(e) => {
                  if (window.innerWidth <= 768) {
                    e.preventDefault();
                    toggleMobileDropdown(e, 'dog');
                  } else {
                    closeMobileMenu();
                  }
                }}
              >
                Dogs <FaChevronDown className="dropdown-caret" />
              </Link>
              <ul className={`dropdown-menu ${mobileDropdown === 'dog' ? 'mobile-open' : ''}`}>
                <li><NavLink to="/category/dog/Biscuits" onClick={closeMobileMenu}>Biscuits</NavLink></li>
                <li><NavLink to="/category/dog/Dry Foods" onClick={closeMobileMenu}>Dry Foods</NavLink></li>
                <li><NavLink to="/category/dog/Cosmetics" onClick={closeMobileMenu}>Cosmetics</NavLink></li>
                <li><NavLink to="/category/dog/Gravy" onClick={closeMobileMenu}>Gravy</NavLink></li>
                <li><NavLink to="/category/dog/Toppings" onClick={closeMobileMenu}>Toppings</NavLink></li>
                <li><NavLink to="/category/dog/Treats" onClick={closeMobileMenu}>Treats</NavLink></li>
                <li><NavLink to="/category/dog/Others" onClick={closeMobileMenu}>Others</NavLink></li>
              </ul>
            </li>
            <li className="nav-item dropdown">
              <Link
                to="/products?filter=Cats"
                className={`nav-link ${isCatActive ? 'active' : ''}`}
                onClick={(e) => {
                  if (window.innerWidth <= 768) {
                    e.preventDefault();
                    toggleMobileDropdown(e, 'cat');
                  } else {
                    closeMobileMenu();
                  }
                }}
              >
                Cats <FaChevronDown className="dropdown-caret" />
              </Link>
              <ul className={`dropdown-menu ${mobileDropdown === 'cat' ? 'mobile-open' : ''}`}>
                <li><NavLink to="/category/cat/Gravy" onClick={closeMobileMenu}>Gravy</NavLink></li>
                <li><NavLink to="/category/cat/Dry Foods" onClick={closeMobileMenu}>Dry Foods</NavLink></li>
                <li><NavLink to="/category/cat/Cosmetics" onClick={closeMobileMenu}>Cosmetics</NavLink></li>
                <li><NavLink to="/category/cat/Treats" onClick={closeMobileMenu}>Treats</NavLink></li>
                <li><NavLink to="/category/cat/Others" onClick={closeMobileMenu}>Others</NavLink></li>
              </ul>
            </li>
            <li className="nav-item" onClick={closeMobileMenu}>
              <NavLink to="/distributors" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Distributors
              </NavLink>
            </li>
            <li className="nav-item" onClick={closeMobileMenu}>
              <NavLink to="/contact" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Contact
              </NavLink>
            </li>
          </ul>

          {/* Search bar inside mobile menu */}
          <SearchBox className="mobile-search-wrapper" onSubmit={handleSearchSubmit} />
        </div>

        {/* Search Bar — Right (desktop) */}
        <SearchBox className="desktop-search-wrapper" onSubmit={handleSearchSubmit} />

        {/* Mobile hamburger */}
        <div className="mobile-toggle" onClick={toggleMobileMenu} aria-label="Toggle menu">
          <div className={`bar ${mobileMenuOpen ? 'open' : ''}`}></div>
          <div className={`bar ${mobileMenuOpen ? 'open' : ''}`}></div>
          <div className={`bar ${mobileMenuOpen ? 'open' : ''}`}></div>
        </div>

      </div>
    </nav>
  );
};

export default Navbar;
